import { Button, Card, Checkbox, Divider, Form, Input, message, notification } from "antd";
import { useForm } from "antd/es/form/Form";
import { pick } from "lodash";
import { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { register } from "../../apis/loginApi";
import { FloatingLabelField } from "../../components/FloatingLabelField";
import { Language } from "../../components/Language";

const Register = () => {
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [agreement, setAgreement] = useState(false);
  const [errorAgreement, setErrorAgreement] = useState("");
  const [errorCapcha, setErrorCapcha] = useState("");
  const { t } = useTranslation();

  const history = useHistory();
  const [form] = useForm();
  const recaptchaRef = useRef(null);
  const { REACT_APP_SITE_KEY_GOOGLE_CAPTCHA } = process.env;

  const onChangeToken = (token) => {
    setCaptchaToken(token);
  };

  const handleRegister = async () => {
    setErrorAgreement(agreement ? "" : t("Please accept Terms and Conditions"));
    setErrorCapcha(captchaToken ? "" : t("Please verify that you are not a robot"));
    const formData = await form.validateFields();

    if (!agreement || !captchaToken) return;

    try {
      setLoading(true);
      await register({ ...formData, captchaToken });
      notification.success({
        message: t("Account created successfully!"),
        description: t(
          "An activation link has been sent to your email. Please check your inbox and click on the link to activate your account."
        ),
        duration: 10,
      });
    } catch (error) {
      message.error(t("An error occurred while saving data"));
    } finally {
      setCaptchaToken("");
      recaptchaRef?.current?.reset();
      setLoading(false);
    }
    window.AirflexJS.logEvent("register", pick(formData, ["email", "fullname"]));
  };

  return (
    <div
      className="w-full h-screen bg-center bg-cover bg-no-repeat flex items-center justify-evenly"
      style={{ backgroundImage: "url('/assets/img/brand/background.svg')" }}
    >
      <div className="hidden lg:block">
        <img src="/assets/img/icons/computer.svg" alt="Computer Icon" width={533} height={537} />
      </div>
      <Card className="w-96 max-h-[92%] overflow-auto">
        <div className="flex justify-between items-center !mb-3">
          <img src="/assets/img/brand/logo.png" alt="logo" className="w-40" />
          <Language compact />
        </div>
        <div className="w-full flex flex-1 flex-col justify-center">
          <div className="text-2xl font-semibold">{t("Sign up")}</div>
          <Form form={form} layout="vertical" requiredMark={false} size="large">
            <Form.Item name="email" className="!mb-2" rules={[{ required: true, type: "email" }]}>
              <FloatingLabelField label={t("Email")}>
                <Input />
              </FloatingLabelField>
            </Form.Item>
            <Form.Item name="fullname" className="!mb-2" rules={[{ required: true }]}>
              <FloatingLabelField label={t("Name")}>
                <Input />
              </FloatingLabelField>
            </Form.Item>
            <Form.Item name="password" className="!mb-2" rules={[{ required: true }, { min: 6 }]}>
              <FloatingLabelField label={t("Password")}>
                <Input.Password />
              </FloatingLabelField>
            </Form.Item>
            <Form.Item
              name="confirm"
              className="!mb-2"
              dependencies={["password"]}
              rules={[
                { required: true },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t("Passwords do not match")));
                  },
                }),
              ]}
            >
              <FloatingLabelField label={t("Confirm password")}>
                <Input.Password />
              </FloatingLabelField>
            </Form.Item>
            <Form.Item name="agreement" className="!mb-2" valuePropName="checked">
              <Checkbox value={agreement} onChange={(e) => setAgreement(e.target.checked)}>
                {t("I have read and agree to the")}&nbsp;
                <a
                  href="https://airflex.io/terms-and-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500"
                >
                  {t("Terms and Conditions")}
                </a>
              </Checkbox>
            </Form.Item>
            {errorAgreement && <div className="text-[#ff4d4f] !mb-3 -mt-5">{errorAgreement}</div>}
            {REACT_APP_SITE_KEY_GOOGLE_CAPTCHA && (
              <div className="!mb-6">
                <ReCAPTCHA sitekey={REACT_APP_SITE_KEY_GOOGLE_CAPTCHA} onChange={onChangeToken} ref={recaptchaRef} />
              </div>
            )}
            {errorCapcha && <div className="text-[#ff4d4f] !mb-3 -mt-6">{errorCapcha}</div>}
            <Button
              type="primary"
              size="large"
              className="w-full bg-gradient-primary"
              onClick={handleRegister}
              loading={loading}
            >
              {t("Sign up")}
            </Button>
          </Form>
          <Divider className="!mt-5 !mb-3">{t("or")}</Divider>
          <div className="text-center">
            {t("Already have an account?")}&nbsp;
            <a
              href="/login"
              className="text-blue-500"
              onClick={(e) => {
                e.preventDefault();
                history.push("/login");
              }}
            >
              {t("Sign in")}
            </a>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Register;
